/**
 * Strategy items that cannot be a single Yes — pause-or-restructure,
 * last ad/keyword in a group, Google recs, keyword pause waiting on
 * search-term refinement — plus agency hygiene disclaimers.
 */
import { AdsWaitState } from '@/components/google-ads/AdsWaitState';
import type {
  AdMonitorResponse,
  ConversionViewResponse,
  StrategyGoogleRec,
  StructureResponse,
} from '@/services/googleAdsManagementService';
import type { SearchTermNegativeRecommendation } from '@/services/googleAdsWeeklyService';
import {
  buildDeepDiveItems,
  buildGoogleRecLookItems,
  buildLookDisclaimers,
  buildLookSearchTermItems,
  buildSearchTermFirstLookItems,
  type DeepDiveItem,
  type ReadFailure,
} from '@/components/google-ads/strategyBoardModel';

// EvidenceRow renders a recommendation as read-only evidence — title, why,
// and an optional note. The G-7 rebuild rule deleted the "Look" button that
// used to sit here (it teleported to an unfiltered keyword list, which is
// bug #8 in the 2026-09-15 rebuild spec). Evidence now sits inline on the
// row until CORE-M-09 lands the fuller "spend / window / conversions"
// treatment.
function EvidenceRow({
  item,
  note,
}: {
  item: DeepDiveItem;
  note?: string;
}) {
  return (
    <div className="px-4 py-3">
      <div className="min-w-0">
        <div className="font-medium">{item.title}</div>
        <p className="mt-1 text-xs text-muted-foreground">{item.why}</p>
        {note ? (
          <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>
        ) : null}
      </div>
    </div>
  );
}

export function StrategyDeepDive({
  ads,
  conversions,
  structure,
  failures,
  readsPending = false,
  googleRecs,
  googleRecsFailed = false,
  googleRecsMessage,
  googleRecsTruncated = false,
  googleRecsShown = 0,
  googleRecsTotal = 0,
  searchTermRecs,
  easyReadsOpen,
}: {
  ads?: AdMonitorResponse;
  conversions?: ConversionViewResponse;
  structure?: StructureResponse;
  // ⛔ VIKTOR (#2248 review): computed once in StrategyBoard from all three
  // read_ok states AND all three query-error flags — this component no
  // longer recomputes it from raw ads/conversions/structure alone, which is
  // exactly what let a failed conversions *fetch* (no read_ok payload at
  // all) slip past here as "nothing to look at."
  failures: ReadFailure[];
  readsPending?: boolean;
  googleRecs?: StrategyGoogleRec[];
  googleRecsFailed?: boolean;
  googleRecsMessage?: string;
  googleRecsTruncated?: boolean;
  googleRecsShown?: number;
  googleRecsTotal?: number;
  searchTermRecs?: SearchTermNegativeRecommendation[];
  easyReadsOpen?: boolean;
}) {
  const readsOpen = easyReadsOpen !== false;
  const items = buildDeepDiveItems({
    ads,
    conversions,
    structure,
    easyReadsOpen: readsOpen,
  });
  const disclaimers = buildLookDisclaimers({ ads, conversions });
  const googleItems = buildGoogleRecLookItems(googleRecs);
  const searchTermFirst = buildSearchTermFirstLookItems(
    structure?.read_ok ? structure.keywords : undefined,
    searchTermRecs,
  );
  const lookTerms = buildLookSearchTermItems(searchTermRecs, readsOpen);

  // Failure banners live above the tabs on StrategyBoard. A failed
  // conversions read must not require opening this tab. Do not claim
  // "nothing to look at" when a read failed — that list is incomplete.
  if (
    items.length === 0 &&
    disclaimers.length === 0 &&
    googleItems.length === 0 &&
    searchTermFirst.length === 0 &&
    lookTerms.length === 0 &&
    !googleRecsFailed
  ) {
    if (failures.length > 0) return null;
    if (readsPending) {
      return (
        <AdsWaitState compact className="px-4" stage="Checking what needs a look" />
      );
    }
    return (
      <p className="px-4 py-3 text-sm text-muted-foreground">
        Nothing that needs a deep dive from the current reads. Easy Yes/No is
        on the other tab.
      </p>
    );
  }

  return (
    <div className="divide-y divide-border">
      {googleRecsFailed ? (
        <div className="border-coral/40 bg-coral-light/20 px-4 py-3">
          <p className="text-sm font-medium text-coral-dark">
            {googleRecsMessage?.trim() ||
              'Could not read Google Ads recommendations for this account'}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            This list is incomplete, not empty.
          </p>
        </div>
      ) : null}
      {disclaimers.length > 0 ? (
        <p className="px-4 pt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Agency hygiene — not a finding
        </p>
      ) : null}
      {disclaimers.map((item) => (
        <EvidenceRow key={item.id} item={item} />
      ))}
      {googleItems.length > 0 ? (
        <p className="px-4 pt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Google recommendations
        </p>
      ) : null}
      {googleItems.map((item) => (
        <EvidenceRow
          key={item.id}
          item={item}
          note="Read only. Open the evidence and decide in Google Ads."
        />
      ))}
      {googleRecsTruncated ? (
        <p className="px-4 py-2 text-xs text-muted-foreground">
          Showing {googleRecsShown} of {googleRecsTotal} Google recommendations.
          The rest stay in Google Ads.
        </p>
      ) : null}
      {searchTermFirst.length > 0 ? (
        <p className="px-4 pt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Search-term refinement first
        </p>
      ) : null}
      {searchTermFirst.map((item) => (
        <EvidenceRow
          key={item.id}
          item={item}
          note="Exclude search terms on Easy, then come back if the keyword is still waste."
        />
      ))}
      {lookTerms.length > 0 ? (
        <p className="px-4 pt-3 text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
          Search terms — confirm before excluding
        </p>
      ) : null}
      {lookTerms.map((item) => (
        <EvidenceRow
          key={item.id}
          item={item}
          note="Open Search Terms if this is junk. Buyer queries stay live."
        />
      ))}
      {items.map((item) => (
        <EvidenceRow
          key={item.id}
          item={item}
          note="Open the evidence and decide pause vs restructure."
        />
      ))}
    </div>
  );
}
